import { useEffect, useMemo, useState } from "react";
import { Search, ChevronDown, ChevronLeft, ChevronRight, X, Star } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../hooks/useToast";
import AdminLayout from "../../components/admin/AdminLayout";
import AdminAvatar from "../../components/admin/AdminAvatar";
import { useAssistantsQuery } from "../../api/query/useAdminQueries";
import { normalizeAssistantListItem } from "../../api/services/assistantsService";

const FALLBACK_AVATAR = "/images/dummyimg.png";

const getFirstValue = (source, keys) => {
  if (!source || typeof source !== "object") return null;
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(source, key) && source[key] != null) {
      return source[key];
    }
  }
  return null;
};

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const cleaned = String(value).replace(/[^0-9.-]/g, "");
  if (!cleaned) return null;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatCurrencyValue = (value) => {
  const amount = toNumber(value);
  if (amount === null || amount <= 0) return "—";
  return `N${amount.toLocaleString()}`;
};

const formatRating = (value) => {
  const amount = toNumber(value);
  if (amount === null || amount <= 0) return "—";
  return amount.toFixed(1);
};

const formatHumanLabel = (value) => {
  if (!value) return "—";
  return String(value)
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const resolveLocation = (item = {}) => {
  const candidates = [
    item.location,
    item.profile?.location,
    item.address,
    item.address?.state,
    item.address?.city,
    item.state,
    item.city,
    item.profile?.state,
    item.profile?.city,
    item.user?.location,
    item.user?.state,
    item.user?.city,
  ];

  for (const candidate of candidates) {
    if (!candidate) continue;
    if (typeof candidate === "string") {
      const value = candidate.trim();
      if (value) return value;
      continue;
    }
    if (typeof candidate === "object") {
      const city =
        candidate.city ||
        candidate.town ||
        candidate.localGovernment ||
        candidate.local_government ||
        candidate.area ||
        candidate.region ||
        candidate.province ||
        candidate.label ||
        candidate.name ||
        candidate.address ||
        candidate.formattedAddress ||
        candidate.formatted_address ||
        "";
      const state =
        candidate.state ||
        candidate.region ||
        candidate.province ||
        candidate.based ||
        candidate.country ||
        "";

      const resolvedCity = typeof city === "string" ? city.trim() : "";
      const resolvedState = typeof state === "string" ? state.trim() : "";
      if (resolvedCity && resolvedState) return `${resolvedCity}, ${resolvedState}`;
      if (resolvedCity) return resolvedCity;
      if (resolvedState) return resolvedState;
    }
  }

  return "—";
};

const resolveVehicle = (item = {}) => {
  const candidates = [item.vehicle, item.profile?.vehicle, item.car, item.vehicleModel, item.vehicle_model];

  for (const candidate of candidates) {
    if (!candidate) continue;
    if (typeof candidate === "string") {
      const value = candidate.trim();
      if (value) return value;
      continue;
    }
    if (typeof candidate === "object") {
      const value = [candidate.brand, candidate.make, candidate.type, candidate.model, candidate.year]
        .filter(Boolean)
        .join(" ")
        .trim();
      if (value) return value;
    }
  }

  return "—";
};

const resolveStatus = (value) => {
  const normalized = String(value || "").toLowerCase();

  if (["active", "approved", "verified", "available", "online", "enabled"].includes(normalized)) {
    return {
      label: "Active",
      className: "bg-[#E4F5E4] text-[#008000]",
    };
  }

  if (["suspended", "blocked", "inactive", "disabled", "rejected"].includes(normalized)) {
    return {
      label: "Suspended",
      className: "bg-[#FDECEC] text-[#FF4040]",
    };
  }

  if (["pending", "in_review", "verification_pending", "awaiting_review", "awaiting_approval"].includes(normalized)) {
    return {
      label: "Pending",
      className: "bg-[#FFF4DB] text-[#F99C07]",
    };
  }

  const label = formatHumanLabel(value);
  return {
    label,
    className: "bg-gray-100 text-gray-700",
  };
};

const normalizeAssistantsPayload = (payload) => {
  const source = payload?.data ?? payload ?? {};
  const items = Array.isArray(source)
    ? source
    : source?.items ||
      source?.results ||
      source?.assistants ||
      source?.providers ||
      source?.data ||
      [];

  if (!Array.isArray(items)) return [];

  return items.map((item, index) => {
    const base = normalizeAssistantListItem(item || {});
    const id =
      base.id ||
      getFirstValue(item, ["id", "_id", "assistantId", "assistant_id", "providerId", "provider_id", "userId", "user_id"]) ||
      getFirstValue(item?.user, ["id", "_id", "userId", "user_id"]) ||
      index;

    const profilePicture =
      item?.profilePicture?.url ||
      item?.profilePicture ||
      item?.avatar ||
      item?.user?.avatar ||
      base.profilePicture?.url ||
      FALLBACK_AVATAR;

    const name =
      base.name ||
      getFirstValue(item, ["name", "fullName", "full_name", "assistantName", "providerName"]) ||
      [item?.user?.firstName, item?.user?.lastName].filter(Boolean).join(" ") ||
      "—";

    const email =
      getFirstValue(item, ["email", "contactEmail", "email_address"]) ||
      item?.user?.email ||
      "—";

    const phone =
      getFirstValue(item, ["phone", "mobile", "phoneNumber", "contactPhone"]) ||
      item?.user?.phone ||
      "—";

    const location = base.location || resolveLocation(item);

    const priceSource = getFirstValue(item, [
      "pricePerDay",
      "price_per_day",
      "price",
      "serviceRate",
      "service_rate",
      "hourRate",
      "hour_rate",
      "dailyRate",
      "daily_rate",
    ]) ?? base.pricePerDay;

    const priceAmount = toNumber(priceSource);
    const price = priceAmount === null || priceAmount <= 0 ? "—" : formatCurrencyValue(priceAmount);

    const ratingSource = getFirstValue(item, [
      "rating",
      "avgRating",
      "averageRating",
      "average_rating",
      "rate",
      "score",
    ]) ?? base.rating;

    const statusSource =
      getFirstValue(item, [
        "status",
        "state",
        "assistantStatus",
        "approvalStatus",
        "verificationStatus",
      ]) ||
      item?.profile?.status ||
      item?.user?.status ||
      item?.profile?.approvalStatus ||
      item?.profile?.verificationStatus;

    const status = resolveStatus(statusSource);

    return {
      id,
      providerId: id,
      name,
      location,
      price,
      statusLabel: status.label,
      statusClassName: status.className,
      hasPendingChanges: Boolean(item?.hasPendingChanges),
      rating: formatRating(ratingSource),
      avatar: profilePicture,
      profilePicture: { url: profilePicture },
      email,
      phone,
      address: location,
      car: resolveVehicle(item),
      raw: item,
    };
  });
};

function AdminAssistants() {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  const queryParams = useMemo(
    () => ({
      page: currentPage,
      limit: 10,
      search: debouncedSearch || undefined,
    }),
    [currentPage, debouncedSearch]
  );

  const { data, isLoading, isError, error } = useAssistantsQuery(queryParams, {
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (isError) {
      toast.apiError(error, "Failed to load assistants.");
    }
  }, [isError, error, toast]);

  const assistants = useMemo(
    () => normalizeAssistantsPayload(data),
    [data]
  );

  const displayAssistants = useMemo(() => {
    if (!searchQuery.trim()) return assistants;
    const term = searchQuery.trim().toLowerCase();
    return assistants.filter((assistant) => {
      const searchable = [
        assistant.name,
        assistant.location,
        assistant.price,
        assistant.statusLabel,
        assistant.rating,
        assistant.email,
        assistant.phone,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return searchable.includes(term);
    });
  }, [assistants, searchQuery]);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Pulo Assistant Management</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-600">
            Manage registered Pulo Assistants, verify credentials and monitor performance rating and tour assignment.
          </p>
        </div>

        <section className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
          <div className="flex flex-col gap-4 border-b border-gray-200 px-6 py-5 md:flex-row md:items-center md:justify-between">
            <h2 className="text-2xl font-bold text-gray-900">Pulo Assistant</h2>

            <div className="relative w-full md:w-[300px]">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search Pulo Assistant"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-full border border-[#C9D3E2] bg-white py-3 pl-12 pr-4 text-sm font-medium text-gray-700 outline-none transition focus:border-[#570033] focus:ring-2 focus:ring-[#570033]/10"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Pulo Assistant</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">
                    Location
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">
                    Price
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">
                    Rate
                  </th>
                </tr>
              </thead>

              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-sm text-gray-500">
                      Loading assistants...
                    </td>
                  </tr>
                ) : displayAssistants.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-sm text-gray-500">
                      No assistants found.
                    </td>
                  </tr>
                ) : (
                  displayAssistants.map((assistant) => (
                    <tr
                      key={assistant.id}
                      className="cursor-pointer border-b border-transparent hover:bg-gray-50"
                      onClick={() => navigate(`/admin/assistants/${assistant.id}`, { state: { assistant } })}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <AdminAvatar
                            src={assistant.avatar}
                            name={assistant.name}
                          />
                          <span className="text-sm font-medium text-gray-900">{assistant.name}</span>
                        </div>
                      </td>

                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{assistant.location}</td>

                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        <span className="whitespace-nowrap">
                          {assistant.price === "—" ? "—" : `${assistant.price}/12hrs`}
                        </span>
                      </td>

                      <td className="px-6 py-4">
                        <div className="flex flex-col items-start gap-1">
                          <span
                            className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${assistant.statusClassName}`}
                          >
                            {assistant.statusLabel}
                          </span>
                          {/* An approved assistant whose post-approval edits are
                              still waiting on a verdict. */}
                          {assistant.hasPendingChanges ? (
                            <span className="inline-flex whitespace-nowrap rounded-full bg-[#EFF8FF] px-3 py-1 text-xs font-medium text-[#0B4A6F]">
                              Changes pending
                            </span>
                          ) : null}
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1 text-sm font-medium text-gray-900">
                          <span>{assistant.rating}</span>
                          <Star className="h-4 w-4 fill-[#F59E0B] text-[#F59E0B]" />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {(() => {
            const pagination = data?.data?.pagination || {
              page: 1,
              limit: 10,
              total: 0,
              totalPages: 0,
            };
            if (pagination.totalPages <= 1) return null;
            return (
              <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4 bg-white">
                <div className="text-sm text-gray-500">
                  Showing <span className="font-semibold text-gray-900">{((pagination.page - 1) * pagination.limit) + 1}</span> to{" "}
                  <span className="font-semibold text-gray-900">
                    {Math.min(pagination.page * pagination.limit, pagination.total)}
                  </span>{" "}
                  of <span className="font-semibold text-gray-900">{pagination.total}</span> assistants
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={pagination.page <= 1}
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    disabled={pagination.page >= pagination.totalPages}
                    onClick={() => setCurrentPage((prev) => Math.min(pagination.totalPages, prev + 1))}
                    className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition"
                  >
                    Next
                  </button>
                </div>
              </div>
            );
          })()}
        </section>
      </div>
    </AdminLayout>
  );
}

export default AdminAssistants;
